/**
 * useAppVersion Hook
 *
 * Liefert Version und Build-Nummer der nativen App aus dem Bundle
 * (iOS: CFBundleShortVersionString / CFBundleVersion, Android:
 * versionName / versionCode). Damit muss die angezeigte Version nicht
 * mehr im Code gepflegt werden.
 *
 * Gibt null zurueck, wenn keine Versionsinfo verfuegbar ist - im Web
 * (dort gibt es kein natives Bundle) und wenn App.getInfo() fehlschlaegt.
 * Aufrufer blenden den Versionsblock dann komplett aus.
 */

import { useEffect, useState } from 'react';
import { App } from '@capacitor/app';
import { isNative } from '@/lib/capacitor';

export interface AppVersion {
  /** Menschenlesbare Version, z.B. "2.1.4" */
  version: string;
  /** Build-Nummer, z.B. "15" */
  build: string;
}

export function useAppVersion(): AppVersion | null {
  const [appVersion, setAppVersion] = useState<AppVersion | null>(null);

  useEffect(() => {
    // Im Web gibt es kein natives Bundle - nichts zu laden.
    if (!isNative) return;

    let cancelled = false;

    App.getInfo()
      .then((info) => {
        if (cancelled) return;
        // Beide Felder muessen da sein, sonst waere die Anzeige unvollstaendig.
        if (!info?.version || !info?.build) return;
        setAppVersion({ version: info.version, build: info.build });
      })
      .catch((error) => {
        // Plugin nicht verfuegbar o.ae. - Versionsblock bleibt ausgeblendet.
        console.warn('[useAppVersion] App.getInfo() failed:', error);
      });

    return () => {
      cancelled = true;
    };
  }, []);

  return appVersion;
}

export default useAppVersion;
